import React, { useContext, useState, useEffect, useRef } from 'react';
import moment from 'moment-timezone';
import { io } from 'socket.io-client';
import { GlobalStateDataContext } from '../../context-api/data-provider/GlobalStateDataProvider';
import ListOfUsers from './ListOfUsers';
import { localStorageKeys } from '../../utils/constants';
import { useNavigate } from 'react-router';

const formattedDateTime = () => {
  return moment().format('MM/DD/YYYY - hh:mm A');
};

const ConnectedUsers = () => {
  const navigate = useNavigate();
  const {
    selectedMenuTab,
    setSelectedMenuTab,
    onLoadRegBranchResponse,
    statsDataFromSocket,
    setStatsDataFromSocket,
    branchSessionLogout,
  } = useContext(GlobalStateDataContext);
  const { TOKEN } = localStorageKeys;

  const socketRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [notificationServerDown, setNotificationServerDown] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const URL_PATH_NOTIFICATION_SERVER = process.env.REACT_APP_NOTIFICATION_SERVER_URL; // WE HAVE TO REPLACE BEFORE GOING GIVE
  const RegionalBranchName =
    onLoadRegBranchResponse?.data?.data?.data?.RegionalBranchInfo?.branchName;
  const [customers, setCustomers] = useState([]);
  const [kitchens, setKitchens] = useState([]);
  const [regionalBranches, setRegionalBranches] = useState([]);

  const FilterUsers = (rooms) => {
    const customers = [];
    const kitchens = [];
    const regionalBranches = [];

    Object.entries(rooms).forEach(([roomName, roomData]) => {
      roomData.users.forEach((user) => {
        if (user.customerEmail) {
          customers.push(user.customerEmail);
        }

        if (user.kitchenName) {
          kitchens.push(user.kitchenName);
        }

        if (user.regionalBranchName) {
          regionalBranches.push(user.regionalBranchName);
        }
      });
    });

    setCustomers([...new Set(customers)]);
    setKitchens([...new Set(kitchens)]);
    setRegionalBranches([...new Set(regionalBranches)]);
  };

  //   console.log('-----customers', customers);
  //   console.log('---kitchens', kitchens);
  //   console.log('---regionalBranches', regionalBranches);

  useEffect(() => {
    const RELOAD_LOCK_KEY = 'regional_socket_reloaded_once';
    let reloadTimer = null;

    const socket = io(URL_PATH_NOTIFICATION_SERVER, {
      reconnection: true,
      reconnectionAttempts: Infinity,
      forceNew: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 2000,
      timeout: 5000,
      query: {
        type: 'regional_frontend_app',
      },
      transports: ['websocket'],
      auth: {
        token: localStorage.getItem(TOKEN),
      },
    });

    socketRef.current = socket;

    const reloadOnceAfterDisconnect = () => {
      const alreadyReloaded = sessionStorage.getItem(RELOAD_LOCK_KEY);

      if (alreadyReloaded) return;

      sessionStorage.setItem(RELOAD_LOCK_KEY, 'true');

      reloadTimer = setTimeout(() => {
        window.location.reload();
      }, 1000);
    };

    const handleRegionalStats = (arg, callback) => {
      try {
        // Open only for debugging
        // console.log(`📶 Pong from Notification Server ${formattedDateTime()}`, arg);

        setLastUpdated(formattedDateTime());
        setStatsDataFromSocket(arg);
        FilterUsers(arg?.rooms || {});

        if (typeof callback === 'function') {
          callback({
            received: true,
            regionalBranchName: RegionalBranchName,
            receivedAt: new Date().toISOString(),
            message: 'Regional frontend received stats successfully',
          });
        }
      } catch (error) {
        console.error('Error processing regional stats:', error);

        if (typeof callback === 'function') {
          callback({
            received: false,
            regionalBranchName: RegionalBranchName,
            error: error.message,
            receivedAt: new Date().toISOString(),
            message: 'Regional frontend failed to process stats',
          });
        }
      }
    };

    socket.on('connect', () => {
      reconnectAttemptsRef.current = 0;
      setConnectionStatus('Connected');
      setNotificationServerDown(false);
      console.log(`Connected ${socket.id} ${formattedDateTime()}`);
    });

    socket.on('disconnect', (reason) => {
      setConnectionStatus('Disconnected: ' + reason);
      setNotificationServerDown(true);

      console.log(`Disconnected ${reason} ${formattedDateTime()}`);

      // Reload only after real disconnect, once
      reloadOnceAfterDisconnect();
    });

    socket.on('reconnect_attempt', (attemptNumber) => {
      reconnectAttemptsRef.current = attemptNumber;
      setConnectionStatus(`Reconnecting... Attempt ${attemptNumber}`);
      console.log(`Reconnection attempt #${attemptNumber}`);
    });

    socket.on('reconnect_failed', () => {
      setConnectionStatus('Reconnection failed');
      setNotificationServerDown(true);
      console.error('Notification server failed to reconnect');
    });

    socket.on('reconnect', () => {
      setNotificationServerDown(false);
      setConnectionStatus('Reconnected successfully');
      console.log('Notification server reconnected successfully');
    });

    socket.on('connect_error', (e) => {
      console.log(`Socket Server is down ${e.message} ${formattedDateTime()}`);
      setNotificationServerDown(true);
    });

    socket.on('socket-auth-error', (data) => {
      //   console.log('Socket auth error:', data);
      if (data?.message === 'Invalid token') {
        branchSessionLogout(navigate); // Logout User
        setNotificationServerDown(true);
      }
    });
    socket.on('regional-stats-update', (stats, callback) => {
      //   console.log('📶 Regional stats received:', stats);

      setLastUpdated(formattedDateTime());
      setStatsDataFromSocket(stats);
      FilterUsers(stats?.rooms || {});

      if (typeof callback === 'function') {
        callback({
          received: true,
          regionalBranchName: RegionalBranchName,
          totalConnected: stats?.totalConnected || 0,
          roomCount: Object.keys(stats?.rooms || {}).length,
          receivedAt: new Date().toISOString(),
          message: 'Regional frontend received stats successfully',
        });
      }
    });

    const pingInterval = setInterval(() => {
      if (socket.connected) {
        socket.emit('ping-from-regionalbranch', {}, (ack) => {
          // Below console is to Testing what data coming from server as stats
          //   console.log('Regional ping ACK:', ack);

          if (ack?.stats) {
            handleRegionalStats(ack.stats);
          }
        });

        console.log(`📡 Sent ping from ConnectedUsers ${formattedDateTime()}`);
      }
    }, 60000);

    socket.on(`pong-${RegionalBranchName}`, handleRegionalStats);
    socket.on(RegionalBranchName, handleRegionalStats);
    socket.on('regional-stats-update', handleRegionalStats);

    return () => {
      if (reloadTimer) clearTimeout(reloadTimer);
      clearInterval(pingInterval);

      socket.off(`pong-${RegionalBranchName}`, handleRegionalStats);
      socket.off(RegionalBranchName, handleRegionalStats);
      socket.off('socket-auth-error');

      socket.disconnect();
    };
  }, [RegionalBranchName]);

  const StyleOne = {
    display: 'flex',
    flexDirection: 'column',
    width: '170px',
    height: '100px',
    border: '1px solid black',
    margin: '10px',
    borderRadius: '5px',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const stores = ['test-store-1', '9001postmankitchen', '1543postmankitchen', 'syedfour'];
  const activeStores = kitchens;

  const offlineStores = stores.filter((store) => !activeStores.includes(store));
  const offlineCount = offlineStores.length;
  const onlineCount = activeStores.length;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div style={{ margin: '10px', color: 'blue' }}>
        {' '}
        URL Socket Admin - <span>https://admin.socket.io </span>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #537D5D',
          width: '560px',
          height: '70px',
          margin: '10px',
          borderRadius: '5px',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#E7EFC7',
        }}
      >
        <div style={{ fontSize: '16px', color: '#091057' }}>
          Updates occur every 1 minute automatically
        </div>
        <div style={{ fontSize: '14px', color: '#537D5D' }}>Last updated : {lastUpdated}</div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ ...StyleOne, width: '370px' }}>
          <div style={{ fontSize: '14px', marginBottom: '5px', fontFamily: 'Inter Bold' }}>
            Stores Status
          </div>
          <div style={{ fontSize: '14px' }}>
            🟢 Online: {onlineCount} | 🔴 Offline: {offlineCount} | Total Stores -{' '}
            {stores?.length}{' '}
          </div>
        </div>
        <div style={StyleOne}>
          <div style={{ fontSize: '16px', marginBottom: '5px' }}> Customers</div>
          <div>
            {' '}
            <span style={{ marginRight: '5px' }}>🟢</span>
            {customers?.length}
          </div>
        </div>
      </div>

      <ListOfUsers customers={customers} stores={stores} activeStores={activeStores} />
    </div>
  );
};

export default ConnectedUsers;
